import { SxProps, Theme } from '@mui/material'

/** Shared visual tokens for catalog-style pages (Data Catalog, Reports, …). */
export const CATALOG_UI = {
	colors: {
		title: '#1F4954',
		body: '#52666C',
		muted: '#A8B4B8',
		section: '#33454B',
		subtle: '#9A9A9A',
		canvas: '#FBFBFC',
		sidebar: '#F7F8F9',
		border: '#E4E4E4',
		cardBorder: '#E6E8EA',
		chipBorder: '#DCDCDC',
		accent: '#F9423A',
		tableIcon: '#E8A33A',
	},
	layout: {
		sidebarWidth: 280,
		contentPx: 4,
		contentPy: 4,
		detailPy: 3,
		cardGridGap: 2,
		sectionGap: 5,
	},
} as const

export const catalogCanvasSx: SxProps<Theme> = {
	height: '100%',
	minHeight: 0,
	overflow: 'auto',
	bgcolor: CATALOG_UI.colors.canvas,
}

export const catalogContentSx: SxProps<Theme> = {
	px: CATALOG_UI.layout.contentPx,
	py: CATALOG_UI.layout.contentPy,
}

export const catalogDetailContentSx: SxProps<Theme> = {
	px: CATALOG_UI.layout.contentPx,
	py: CATALOG_UI.layout.detailPy,
	pb: 10,
}

export const catalogPageBadgeSx: SxProps<Theme> = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 0.75,
	mb: 2,
	px: 1.5,
	py: 0.5,
	borderRadius: '999px',
	fontSize: 11,
	fontWeight: 700,
	letterSpacing: '0.06em',
	textTransform: 'uppercase',
}

export const catalogPageTitleSx: SxProps<Theme> = {
	mb: 1.5,
	fontSize: 28,
	fontWeight: 800,
	letterSpacing: '-0.01em',
	lineHeight: 1.2,
	color: CATALOG_UI.colors.title,
}

export const catalogPageDescriptionSx: SxProps<Theme> = {
	maxWidth: 640,
	fontSize: 14,
	lineHeight: 1.6,
	color: CATALOG_UI.colors.body,
}

export const catalogSectionTitleSx: SxProps<Theme> = {
	fontSize: 14,
	fontWeight: 700,
	color: CATALOG_UI.colors.section,
}

export const catalogSectionCountSx: SxProps<Theme> = {
	fontSize: 12,
	fontWeight: 400,
	color: CATALOG_UI.colors.muted,
}

export const catalogCardTitleSx: SxProps<Theme> = {
	flex: 1,
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	fontSize: 15,
	fontWeight: 700,
	color: CATALOG_UI.colors.title,
}

export const catalogCardDescriptionSx: SxProps<Theme> = {
	mb: 1.5,
	minHeight: 36,
	overflow: 'hidden',
	display: '-webkit-box',
	WebkitLineClamp: 2,
	WebkitBoxOrient: 'vertical',
	fontSize: 13,
	lineHeight: 1.5,
	color: CATALOG_UI.colors.body,
}

export const catalogDetailTitleSx: SxProps<Theme> = {
	fontSize: 26,
	fontWeight: 800,
	letterSpacing: '-0.01em',
	lineHeight: 1.2,
	color: CATALOG_UI.colors.title,
}

export const catalogBreadcrumbButtonSx: SxProps<Theme> = {
	mb: 2.5,
	px: 0,
	minWidth: 0,
	color: CATALOG_UI.colors.title,
	textTransform: 'none',
	fontWeight: 600,
	fontSize: 13,
	'&:hover': {
		bgcolor: 'transparent',
		textDecoration: 'underline',
	},
}

export const catalogSidebarSx: SxProps<Theme> = {
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	flexShrink: 0,
	height: '100%',
	borderRight: `1px solid ${CATALOG_UI.colors.border}`,
	bgcolor: CATALOG_UI.colors.sidebar,
}

export const catalogCardSx = (active: boolean): SxProps<Theme> => ({
	height: '100%',
	minHeight: 148,
	borderRadius: '14px',
	borderColor: active
		? CATALOG_UI.colors.title
		: CATALOG_UI.colors.cardBorder,
	boxShadow: active ? '0 4px 16px rgba(31,73,84,0.10)' : 'none',
	bgcolor: '#fff',
	transition: 'border-color 0.12s, box-shadow 0.12s, transform 0.1s',
	'&:hover': {
		borderColor: CATALOG_UI.colors.title,
		boxShadow: '0 4px 16px rgba(31,73,84,0.10)',
		transform: 'translateY(-1px)',
	},
})
